
import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JsBarcode from 'jsbarcode';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Barcode Report Display Component
const BarcodeReportDisplay = ({ filters, branchProfile, reportData, reportType, type }) => {
  const reportTitle = `Barcode List Report (${type === 'productwise' ? 'Productwise' : 'Groupwise'} - ${reportType === 'summary' ? 'Summary' : 'Detailed'})`;
  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  const generateBarcodeImage = (barcode) => {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, barcode, {
      format: 'CODE128',
      width: 1,
      height: 40,
      displayValue: false,
      margin: 0
    });
    return canvas.toDataURL('image/png');
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {reportTitle}
            </div>
            <div style={{ marginTop: '10px' }}>
              <strong>Products:</strong> {filters.products.includes("All Products") ? "All Products" : filters.products.join(", ")}
            </div>
            <div>
              <strong>Groups:</strong> {filters.groups.includes("All Groups") ? "All Groups" : filters.groups.join(", ")}
            </div>
            <div>
              <strong>Brands:</strong> {filters.brands.includes("All Brands") ? "All Brands" : filters.brands.join(", ")}
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {reportType === 'detailed' ? 'Detail' : 'Summary'}
          </div>
        </div>
      </div>

      {/* Report Data Table */}
      {type === 'productwise' ? (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '11px',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
              {reportType === 'detailed' && (
                <>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice No</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
                </>
              )}
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Barcode</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRP</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {reportData.length === 0 ? (
              <tr>
                <td colSpan={reportType === 'detailed' ? 7 : 5} style={{
                  border: '1px solid #cbd5e1',
                  padding: '30px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: '#f8fafc'
                }}>
                  No data found for the selected criteria
                </td>
              </tr>
            ) : (
              reportData.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
                  {reportType === 'detailed' && (
                    <>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceNo || 'N/A'}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceDate || 'N/A'}</td>
                    </>
                  )}
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                    <img src={generateBarcodeImage(item.barCode)} alt="Barcode" style={{ height: '40px' }} />
                    <div>{item.barCode}</div>
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.mrp || 0).toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.qty || 0).toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : (
        // Groupwise Report
        <div>
          {Object.entries(reportData).map(([group, items], groupIndex) => (
            <div key={group} style={{ marginBottom: '30px' }}>
              <h2 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#3b82f6',
                marginBottom: '15px'
              }}>
                Group: {group}
              </h2>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px',
                border: '2px solid #3b82f6',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
                    <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
                    <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
                    {reportType === 'detailed' && (
                      <>
                        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice No</th>
                        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
                      </>
                    )}
                    <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Barcode</th>
                    <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRP</th>
                    <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={reportType === 'detailed' ? 7 : 5} style={{
                        border: '1px solid #cbd5e1',
                        padding: '30px',
                        textAlign: 'center',
                        color: '#64748b',
                        fontStyle: 'italic',
                        fontSize: '14px',
                        backgroundColor: '#f8fafc'
                      }}>
                        No data found for this group
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
                        {reportType === 'detailed' && (
                          <>
                            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceNo || 'N/A'}</td>
                            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceDate || 'N/A'}</td>
                          </>
                        )}
                        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                          <img src={generateBarcodeImage(item.barCode)} alt="Barcode" style={{ height: '40px' }} />
                          <div>{item.barCode}</div>
                        </td>
                        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.mrp || 0).toFixed(2)}</td>
                        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.qty || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Barcode List Report Generator Component
const BarcodeListReportGenerator = () => {
  const [filters, setFilters] = useState({
    products: ['All Products'],
    groups: ['All Groups'],
    brands: ['All Brands']
  });
  const [products, setProducts] = useState(['All Products']);
  const [groups, setGroups] = useState(['All Groups']);
  const [brands, setBrands] = useState(['All Brands']);
  const [type, setType] = useState('');
  const [reportType, setReportType] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: 'SUPREME TRADERS',
    address: '',
    phone: '',
    city: ''
  });
  const [reportData, setReportData] = useState([]);
  const [rawInvoiceData, setRawInvoiceData] = useState([]);
  const [productData, setProductData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || 'SUPREME TRADERS',
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    // Load Products
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productList = Object.entries(data).map(([id, product]) => ({
          id,
          productName: product.productName || 'Unknown Product',
          group: product.group || 'General',
          brand: product.brand || 'Generic',
          barcode: product.barcode || 'N/A',
          mrp: parseFloat(product.mrp) || 0
        }));
        setProductData(productList);
        setProducts(['All Products', ...new Set(productList.map(p => p.productName))]);
        setGroups(['All Groups', ...new Set(productList.map(p => p.group))]);
        setBrands(['All Brands', ...new Set(productList.map(p => p.brand))]);
      } else {
        setProductData([]);
        setProducts(['All Products']);
        setGroups(['All Groups']);
        setBrands(['All Brands']);
      }
    });

    // Load Invoice Data
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    onValue(invoicesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invoiceList = Object.entries(data).flatMap(([id, invoice]) =>
          (invoice.items || []).map(item => ({
            id,
            invoiceNo: invoice.supplierDetails.invoiceNo || 'N/A',
            invoiceDate: invoice.supplierDetails.invoiceDate || 'N/A',
            productName: item.item || 'N/A',
            barCode: item.barcode || item.barCode || 'N/A',
            mrp: parseFloat(item.mrp) || 0,
            qty: parseFloat(item.qty) || 0
          }))
        );
        setRawInvoiceData(invoiceList);
      } else {
        setRawInvoiceData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(productsRef);
      off(invoicesRef);
    };
  };

  // Process Report Data
  useEffect(() => {
    if (!type || !reportType) {
      setReportData([]);
      return;
    }

    // Filter products by selected filters
    const filteredProducts = productData.filter(product => 
      (filters.products.includes('All Products') || filters.products.includes(product.productName)) &&
      (filters.groups.includes('All Groups') || filters.groups.includes(product.group)) &&
      (filters.brands.includes('All Brands') || filters.brands.includes(product.brand))
    );

    if (type === 'productwise') {
      if (reportType === 'summary') {
        // Productwise Summary: Latest MRP and barcode per product
        const productMap = {};
        filteredProducts.forEach(product => {
          const invoicesForProduct = rawInvoiceData.filter(inv => inv.productName === product.productName);
          const latestInvoice = invoicesForProduct.sort((a, b) => 
            new Date(b.invoiceDate.split('-').reverse().join('-')) - 
            new Date(a.invoiceDate.split('-').reverse().join('-'))
          )[0] || {};
          
          productMap[product.productName] = {
            productName: product.productName,
            barCode: product.barcode,
            mrp: latestInvoice.mrp || product.mrp || 0,
            qty: invoicesForProduct.reduce((sum, inv) => sum + inv.qty, 0)
          };
        });
        setReportData(Object.values(productMap).sort((a, b) => a.productName.localeCompare(b.productName)));
      } else {
        // Productwise Detailed: All invoice entries for filtered products
        const filteredInvoices = rawInvoiceData.filter(inv => 
          filteredProducts.some(p => p.productName === inv.productName)
        );
        setReportData(filteredInvoices.sort((a, b) => a.productName.localeCompare(b.productName)));
      }
    } else {
      // Groupwise Report
      const groupMap = {};
      filteredProducts.forEach(product => {
        const invoicesForProduct = rawInvoiceData.filter(inv => inv.productName === product.productName);
        if (!groupMap[product.group]) {
          groupMap[product.group] = [];
        }
        if (reportType === 'summary') {
          const latestInvoice = invoicesForProduct.sort((a, b) => 
            new Date(b.invoiceDate.split('-').reverse().join('-')) - 
            new Date(a.invoiceDate.split('-').reverse().join('-'))
          )[0] || {};
          groupMap[product.group].push({
            productName: product.productName,
            barCode: product.barcode,
            mrp: latestInvoice.mrp || product.mrp || 0,
            qty: invoicesForProduct.reduce((sum, inv) => sum + inv.qty, 0)
          });
        } else {
          invoicesForProduct.forEach(inv => {
            groupMap[product.group].push({
              productName: inv.productName,
              invoiceNo: inv.invoiceNo,
              invoiceDate: inv.invoiceDate,
              barCode: inv.barCode,
              mrp: inv.mrp,
              qty: inv.qty
            });
          });
        }
      });
      setReportData(groupMap);
    }
  }, [filters.products, filters.groups, filters.brands, type, reportType, rawInvoiceData, productData]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    // More specific validation with individual checks
    if (!type && !reportType) {
      alert('Please select both Type and Report Type.');
      return;
    }
    if (!type) {
      alert('Please select a Type.');
      return;
    }
    if (!reportType) {
      alert('Please select a Report Type.');
      return;
    }
    
    // Additional check to ensure they're not empty strings
    if (type === '' || reportType === '') {
      alert('Please select both Type and Report Type.');
      return;
    }
    
    setShowReport(true);
  };

  const handlePrint = () => {
    if (!showReport) {
      alert('Please generate the report first.');
      return;
    }
  
    const printContent = (
      <BarcodeReportDisplay 
        filters={filters}
        branchProfile={branchProfile}
        reportData={reportData}
        reportType={reportType}
        type={type}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode List Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              background: white;
            }
            @page { 
              size: A4; 
              margin: 10mm; 
            }
            table { 
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
              margin-bottom: 20px;
            }
            thead {
              display: table-header-group;
            }
            tbody {
              display: table-row-group;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              border: 1px solid #cbd5e1;
              padding: 8px;
              text-align: left;
              vertical-align: top;
            }
            th {
              background-color: #f1f5f9 !important;
              font-weight: 600;
              text-align: center;
            }
            img { 
              max-height: 40px; 
              width: auto;
              display: block;
              margin: 0 auto;
            }
            .print-content {
              width: 100%;
              max-width: none;
            }
            h1, h2 {
              color: #1e293b;
              margin-bottom: 10px;
            }
            .header-section {
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 2px solid #e2e8f0;
            }
            .group-header {
              font-size: 16px;
              font-weight: 600;
              color: #3b82f6;
              margin: 20px 0 10px 0;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 15px; 
              }
              table { 
                width: 100% !important;
                font-size: 10px;
              }
              th, td { 
                padding: 6px 4px;
                border: 1px solid rgb(203, 213, 225) !important;
              }
              .no-print { 
                display: none !important; 
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-content">${htmlString}</div>
          <script>
            window.addEventListener('load', function() {
              setTimeout(function() {
                window.print();
              }, 1000);
            });
            
            window.addEventListener('afterprint', function() {
              window.close();
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadExcel = () => {
    let sheetData = [];
    if (type === 'productwise') {
      sheetData = reportData.map((item, index) => ({
        'S.No': index + 1,
        'Product Name': item.productName,
        ...(reportType === 'detailed' && {
          'Invoice No': item.invoiceNo || 'N/A',
          'Date': item.invoiceDate || 'N/A'
        }),
        'Barcode': item.barCode,
        'MRP': parseFloat(item.mrp || 0).toFixed(2),
        'Quantity': parseFloat(item.qty || 0).toFixed(2)
      }));
    } else {
      sheetData = Object.entries(reportData).flatMap(([group, items], groupIndex) =>
        items.map((item, index) => ({
          'Group': group,
          'S.No': index + 1,
          'Product Name': item.productName,
          ...(reportType === 'detailed' && {
            'Invoice No': item.invoiceNo || 'N/A',
            'Date': item.invoiceDate || 'N/A'
          }),
          'Barcode': item.barCode,
          'MRP': parseFloat(item.mrp || 0).toFixed(2),
          'Quantity': parseFloat(item.qty || 0).toFixed(2)
        }))
      );
    }

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Barcode Report');
    XLSX.writeFile(workbook, `BarcodeListReport_${type}_${reportType}.xlsx`);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFont('Arial', 'normal');
    doc.setFontSize(12);

    // Header
    doc.text(branchProfile.branchName, 10, 10);
    doc.setFontSize(10);
    doc.text(`Barcode List Report (${type === 'productwise' ? 'Productwise' : 'Groupwise'} - ${reportType === 'summary' ? 'Summary' : 'Detailed'})`, 10, 20);
    doc.text(`Products: ${filters.products.includes("All Products") ? "All Products" : filters.products.join(", ")}`, 10, 30);
    doc.text(`Groups: ${filters.groups.includes("All Groups") ? "All Groups" : filters.groups.join(", ")}`, 10, 40);
    doc.text(`Brands: ${filters.brands.includes("All Brands") ? "All Brands" : filters.brands.join(", ")}`, 10, 50);
    doc.text(branchProfile.city || getCityFromAddress(branchProfile.address), 180, 10, { align: 'right' });

    // Report Data Table
    if (type === 'productwise') {
      autoTable(doc, {
        startY: 60,
        head: [['S.No', 'Product Name', ...(reportType === 'detailed' ? ['Invoice No', 'Date'] : []), 'Barcode', 'MRP', 'Quantity']],
        body: reportData.map((item, index) => [
          index + 1,
          item.productName,
          ...(reportType === 'detailed' ? [item.invoiceNo || 'N/A', item.invoiceDate || 'N/A'] : []),
          item.barCode,
          parseFloat(item.mrp || 0).toFixed(2),
          parseFloat(item.qty || 0).toFixed(2)
        ]),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: reportType === 'detailed' ? 40 : 60 },
          ...(reportType === 'detailed' && {
            2: { cellWidth: 20 },
            3: { cellWidth: 25 }
          }),
          [reportType === 'detailed' ? 4 : 2]: { cellWidth: 30 },
          [reportType === 'detailed' ? 5 : 3]: { cellWidth: 20, halign: 'right' },
          [reportType === 'detailed' ? 6 : 4]: { cellWidth: 20, halign: 'right' }
        }
      });
    } else {
      let yOffset = 60;
      Object.entries(reportData).forEach(([group, items]) => {
        doc.setFontSize(11);
        doc.text(`Group: ${group}`, 10, yOffset);
        yOffset += 10;
        autoTable(doc, {
          startY: yOffset,
          head: [['S.No', 'Product Name', ...(reportType === 'detailed' ? ['Invoice No', 'Date'] : []), 'Barcode', 'MRP', 'Quantity']],
          body: items.map((item, index) => [
            index + 1,
            item.productName,
            ...(reportType === 'detailed' ? [item.invoiceNo || 'N/A', item.invoiceDate || 'N/A'] : []),
            item.barCode,
            parseFloat(item.mrp || 0).toFixed(2),
            parseFloat(item.qty || 0).toFixed(2)
          ]),
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: reportType === 'detailed' ? 40 : 60 },
            ...(reportType === 'detailed' && {
              2: { cellWidth: 20 },
              3: { cellWidth: 25 }
            }),
            [reportType === 'detailed' ? 4 : 2]: { cellWidth: 30 },
            [reportType === 'detailed' ? 5 : 3]: { cellWidth: 20, halign: 'right' },
            [reportType === 'detailed' ? 6 : 4]: { cellWidth: 20, halign: 'right' }
          }
        });
        yOffset = doc.lastAutoTable.finalY + 10;
      });
    }

    doc.save(`BarcodeListReport_${type}_${reportType}.pdf`);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Barcode Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Barcode List Report </p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media print {
            body * { visibility: hidden; }
            .print-only, .print-only * { visibility: visible; }
            .print-only { 
              position: absolute; 
              left: 0; 
              top: 0; 
              width: 100%; 
              margin: 0; 
              padding: 20px; 
              box-shadow: none; 
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Barcode List Report </h1>
      </div>
      
      {/* Filter Inputs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <FilterSection 
          title="Products" 
          options={products} 
          selected={filters.products} 
          onChange={(value) => handleFilterChange('products', value)}
        />
        <FilterSection 
          title="Groups" 
          options={groups} 
          selected={filters.groups} 
          onChange={(value) => handleFilterChange('groups', value)}
        />
        <FilterSection 
          title="Brands" 
          options={brands} 
          selected={filters.brands} 
          onChange={(value) => handleFilterChange('brands', value)}
        />
      </div>

      {/* Type and Report Type Selection */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="">Select Type</option>
            <option value="productwise">Productwise</option>
          
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="">Select Report Type</option>
            <option value="summary">Summary</option>
            
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handleDownloadExcel}
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download Excel
        </button>
        <button
          onClick={handleDownloadPDF}
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download PDF
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Barcode Report Display */}
      {showReport && (
        <BarcodeReportDisplay 
          filters={filters}
          branchProfile={branchProfile}
          reportData={reportData}
          reportType={reportType}
          type={type}
        />
      )}
    </div>
  );
};

export default BarcodeListReportGenerator;